import React, { useState, useEffect } from 'react';
import { Table, Card, Space, Tag, Typography, Input, Select, Button, message, Modal, Form, Checkbox } from 'antd';
import { MailOutlined, SearchOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  border: 1px solid ${COLORS.border};
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const InternalEmails: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [filters, setFilters] = useState({
    type: '',
    recipientEmail: '',
    senderEmail: '',
  });
  const [sendBulkEmailModalVisible, setSendBulkEmailModalVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulkForm] = Form.useForm();

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    fetchEmails();
  }, [pagination.current, pagination.pageSize]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        perPage: pagination.pageSize,
      };

      if (filters.type) {
        params.type = filters.type;
      }
      if (filters.recipientEmail) {
        params.recipientEmail = filters.recipientEmail;
      }
      if (filters.senderEmail) {
        params.senderEmail = filters.senderEmail;
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_INTERNAL_EMAILS, { params });
      if (response.data?.status === 'success' && response.data?.data) {
        setEmails(response.data.data.emails || []);
        setTotal(response.data.data.pagination?.total || 0);
      }
    } catch (error: any) {
      message.error('Failed to fetch internal emails');
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchEmails();
  };

  const handleReset = () => {
    setFilters({
      type: '',
      recipientEmail: '',
      senderEmail: '',
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => fetchEmails(), 100);
  };

  const handleSendBulkEmail = async (values: any) => {
    // Validate at least one group is selected
    if (!values.sendToAdmins && !values.sendToMerchants) {
      message.error('Please select at least one recipient group');
      return;
    }
    
    setSending(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SEND_BULK_EMAIL, {
        subject: values.subject,
        message: values.message,
        sendToAdmins: values.sendToAdmins || false,
        sendToMerchants: values.sendToMerchants || false,
      });
      if (response.data?.status === 'success') {
        const data = response.data.data;
        const recipients = [];
        if (values.sendToAdmins) recipients.push('admins');
        if (values.sendToMerchants) recipients.push('merchants');
        message.success(`Bulk email sent to ${recipients.join(' & ')}: ${data.success} success, ${data.failed} failed`);
        if (data.errors && data.errors.length > 0) {
          console.error('Email errors:', data.errors);
        }
        setSendBulkEmailModalVisible(false);
        bulkForm.resetFields();
        fetchEmails();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send bulk email');
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          BIRTHDAY: 'purple',
          INTERNAL_MESSAGE: 'blue',
          BULK_EMAIL: 'orange',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Recipient',
      dataIndex: 'recipientEmail',
      key: 'recipientEmail',
      render: (email: string, record: any) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{email}</div>
          {record.recipientUserType && (
            <Tag color={record.recipientUserType === 'Administrator' ? 'blue' : 'green'} style={{ marginTop: 4 }}>
              {record.recipientUserType}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Sender',
      dataIndex: 'senderEmail',
      key: 'senderEmail',
      render: (email: string, record: any) => email ? (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{email}</div>
          {record.senderUserType && (
            <Tag color={record.senderUserType === 'Administrator' ? 'blue' : 'green'} style={{ marginTop: 4 }}>
              {record.senderUserType}
            </Tag>
          )}
        </div>
      ) : <Text type="secondary">System</Text>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Sent',
      dataIndex: 'emailSent',
      key: 'emailSent',
      render: (sent: boolean) => (
        <Tag color={sent ? 'green' : 'red'}>{sent ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'emailSentAt',
      key: 'emailSentAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          <MailOutlined /> Internal Emails
        </Title>
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setSendBulkEmailModalVisible(true)}
          >
            Send Bulk Email
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchEmails}>
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard>
        <FilterContainer>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Select
                placeholder="Filter by type"
                value={filters.type || undefined}
                onChange={(value) => setFilters({ ...filters, type: value || '' })}
                style={{ width: 200 }}
                allowClear
              >
                <Option value="BIRTHDAY">Birthday</Option>
                <Option value="INTERNAL_MESSAGE">Internal Message</Option>
                <Option value="BULK_EMAIL">Bulk Email</Option>
              </Select>
              <Input
                placeholder="Filter by recipient email"
                value={filters.recipientEmail}
                onChange={(e) => setFilters({ ...filters, recipientEmail: e.target.value })}
                style={{ width: 300, maxWidth: '100%' }}
                prefix={<SearchOutlined />}
                onPressEnter={handleSearch}
              />
              <Input
                placeholder="Filter by sender email"
                value={filters.senderEmail}
                onChange={(e) => setFilters({ ...filters, senderEmail: e.target.value })}
                style={{ width: 300, maxWidth: '100%' }}
                prefix={<SearchOutlined />}
                onPressEnter={handleSearch}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </Space>
        </FilterContainer>

        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={emails}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: window.innerWidth > 768,
              showQuickJumper: window.innerWidth > 768,
              showTotal: (total) => `Total ${total} internal emails`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </StyledCard>

      {/* Send Bulk Email Modal */}
      <Modal
        title="Send Bulk Email"
        open={sendBulkEmailModalVisible}
        onCancel={() => {
          setSendBulkEmailModalVisible(false);
          bulkForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={bulkForm}
          layout="vertical"
          onFinish={handleSendBulkEmail}
          initialValues={{ sendToAdmins: true, sendToMerchants: true }}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Select which user groups should receive this email.
          </Text>
          <Form.Item
            label="Recipients"
            required
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" size={8}>
              <Form.Item name="sendToAdmins" valuePropName="checked" noStyle>
                <Checkbox>
                  <Text style={{ color: COLORS.textPrimary }}>Send to All Administrators</Text>
                </Checkbox>
              </Form.Item>
              <Form.Item name="sendToMerchants" valuePropName="checked" noStyle>
                <Checkbox>
                  <Text style={{ color: COLORS.textPrimary }}>Send to All Merchants</Text>
                </Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
          >
            <Input placeholder="Email subject" />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter message' }]}
          >
            <TextArea rows={6} placeholder="Email message content" />
          </Form.Item>
          <Form.Item
            shouldUpdate={(prev: any, cur: any) =>
              prev.sendToAdmins !== cur.sendToAdmins || prev.sendToMerchants !== cur.sendToMerchants
            }
          >
            {({ getFieldValue }: any) => (
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={sending}
                  disabled={!getFieldValue('sendToAdmins') && !getFieldValue('sendToMerchants')}
                >
                  Send Bulk Email
                </Button>
                <Button onClick={() => {
                  setSendBulkEmailModalVisible(false);
                  bulkForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default InternalEmails;

